import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, Download, Printer } from "lucide-react";
import {
  INVOICE_STATUS_DEFINITIONS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_DEFINITIONS,
  DEFAULT_INVOICE_PRESENTATION,
  parseMajorToMinor,
  resolveDocumentLanguage,
  type InvoiceAction,
  type InvoicePresentation,
  type InvoiceViewModel,
  type PaymentMethod,
} from "@laundrypro/shared";
import {
  Badge, Button, Card, CardHeader, EmptyState, ErrorText, Input, Modal,
  PageHeader, Select, Skeleton, useToast,
} from "../components/ui";
import { InvoiceDocument, type InvoiceFormat } from "../components/invoice/InvoiceDocument";
import { PrintPortal } from "../components/invoice/PrintPortal";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch, downloadFile } from "../lib/api";
import { currencyLabel, money } from "../lib/format";

type InvoiceDetail = InvoiceViewModel & { allowedActions: InvoiceAction[] };

/**
 * Owner-controlled presentation settings. They affect RENDERING only.
 *
 * The server returns the full `InvoicePresentation` contract alongside the
 * footers and default format, so this screen hands the document the SAME
 * object the server PDF template receives — one setting, one result on every
 * surface.
 */
interface InvoiceSettings extends InvoicePresentation {
  footerTextAr: string | null;
  footerTextEn: string | null;
  receiptLanguage: "ar" | "en" | "both";
  defaultFormat: InvoiceFormat | "a4_pdf" | "thermal_80" | "thermal_58";
}

/** Picks just the presentation fields out of the settings payload. */
function toPresentation(s: InvoiceSettings | null): InvoicePresentation {
  if (!s) return DEFAULT_INVOICE_PRESENTATION;
  return {
    showLogo: s.showLogo, logoSize: s.logoSize, logoAlign: s.logoAlign,
    showBusinessName: s.showBusinessName, showVatNumber: s.showVatNumber,
    showAddress: s.showAddress, showDatetime: s.showDatetime,
    showCustomer: s.showCustomer, showPaymentMethod: s.showPaymentMethod,
    showTaxDetail: s.showTaxDetail, showNotes: s.showNotes,
    showQr: s.showQr, qrSize: s.qrSize, qrPosition: s.qrPosition,
  };
}

/** The stored enum uses snake names; the renderer uses its own union. */
function toFormat(stored: string | undefined): InvoiceFormat {
  if (stored === "thermal_80") return "thermal80";
  if (stored === "thermal_58") return "thermal58";
  return "a4";
}

const FORMATS: { key: InvoiceFormat; ar: string; en: string }[] = [
  { key: "a4", ar: "A4", en: "A4" },
  { key: "thermal80", ar: "حراري ٨٠مم", en: "Thermal 80mm" },
  { key: "thermal58", ar: "حراري ٥٨مم", en: "Thermal 58mm" },
];

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  // No `branding` here on purpose: everything this screen prints comes from
  // the invoice's own snapshot, never from the business's current identity.
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";
  const currency = currencyLabel(language);

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [settings, setSettings] = useState<InvoiceSettings | null>(null);
  const [format, setFormat] = useState<InvoiceFormat>("a4");
  const [printing, setPrinting] = useState<InvoiceFormat | null>(null);

  /**
   * The DOCUMENT's language, which is not the app's language.
   *
   * `language` decides the chrome around the preview — headings, buttons,
   * status chips. What gets printed is the owner's `receiptLanguage`, resolved
   * through the same shared function the server PDF uses. Without this the
   * browser print and the PDF of one invoice could come out in two different
   * languages purely because of who was signed in.
   */
  const docLanguage = resolveDocumentLanguage(settings?.receiptLanguage);
  const docFooter = docLanguage === "ar" ? settings?.footerTextAr : settings?.footerTextEn;

  /**
   * Auto-print, arriving from a completed Direct Sale as `?autoprint=1`.
   *
   * Fired once, and only once both the invoice and the owner's print settings
   * have loaded — printing before `settings` resolves would use the wrong
   * default format. The ref makes it idempotent so a re-render cannot open a
   * second print dialog.
   */
  const autoPrinted = useRef(false);
  useEffect(() => {
    if (autoPrinted.current) return;
    if (searchParams.get("autoprint") !== "1") return;
    if (!invoice || !settings) return;
    autoPrinted.current = true;
    setPrinting(toFormat(settings.defaultFormat as string));
  }, [searchParams, invoice, settings]);

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenReason, setRegenReason] = useState("");
  const [regenError, setRegenError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<PaymentMethod>("CASH");
  const [payError, setPayError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoadError(null);
    try {
      setInvoice(await apiFetch<InvoiceDetail>(`/invoices/${id}`));
    } catch (err) {
      setLoadError(
        err instanceof ApiError && err.status === 404
          ? ar ? "الفاتورة غير موجودة." : "This invoice does not exist."
          : err instanceof ApiError ? err.message
          : ar ? "تعذّر تحميل الفاتورة." : "Couldn't load this invoice.",
      );
    }
  }, [id, ar]);

  useEffect(() => { void load(); }, [load]);

  // Presentation settings are fetched separately and are allowed to fail:
  // a missing footer must never block viewing a financial document. The
  // owner's default format seeds the picker on first load only, so it does
  // not fight the user's choice afterwards.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await apiFetch<InvoiceSettings>("/settings/invoice");
        if (cancelled) return;
        setSettings(s);
        setFormat(toFormat(s.defaultFormat as string));
      } catch {
        if (!cancelled) setSettings(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /** Every mutation reloads afterwards: a refusal usually means it changed. */
  const run = async (label: string, fn: () => Promise<unknown>, success: string) => {
    setActionError(null);
    setBusy(label);
    try {
      await fn();
      showToast("success", success);
      await load();
      return true;
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : ar ? "تعذّر إتمام العملية." : "That didn't work.");
      await load();
      return false;
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "الفواتير" : "Invoices"} />
        <Card>
          <EmptyState
            icon={<AlertTriangle className="size-6" />}
            title={ar ? "تعذّر تحميل الفاتورة" : "Couldn't load this invoice"}
            description={loadError}
            action={<Button size="sm" onClick={load}>{ar ? "إعادة المحاولة" : "Retry"}</Button>}
          />
        </Card>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-44" />
        <Card><Skeleton className="h-96 w-full" /></Card>
      </div>
    );
  }

  const def = INVOICE_STATUS_DEFINITIONS[invoice.status];
  const can = (a: InvoiceAction) => invoice.allowedActions.includes(a);
  // Server truth AND permission: the button only appears when both agree.
  const canIssue = can("issue") && hasPermission("invoices.issue");
  const canVoid = can("void") && hasPermission("invoices.void");
  const canDelete = can("delete") && hasPermission("invoices.delete");
  const canPay = can("record_payment") && hasPermission("payments.create");
  const canPrint = hasPermission("invoices.print");
  const canDownloadPdf = hasPermission("invoices.download");
  // Regeneration only makes sense once something is archived — a draft has
  // nothing to regenerate, matching the server's own refusal.
  const canRegeneratePdf =
    (invoice.status === "ISSUED" || invoice.status === "VOIDED") &&
    hasPermission("invoices.regenerate_pdf");

  /*
   * The invoice's OWN logo, from its seller snapshot — deliberately not the
   * business's current branding logo.
   *
   * No fallback to current branding: if this invoice was issued without a
   * logo, the honest rendering is no logo. Substituting today's mark would
   * put something on the document that was never on it, and would disagree
   * with the archived PDF, which has always used the snapshot.
   */
  const logoUrl = invoice.seller.logoUrl ?? null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={invoice.invoiceRef || (ar ? "مسودة فاتورة" : "Draft invoice")}
        subtitle={invoice.customer.name ?? undefined}
        trailing={<Badge tone={def.tone}>{ar ? def.labelAr : def.labelEn}</Badge>}
      />

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          title={ar ? "الإجراءات" : "Actions"}
          description={ar ? def.descriptionAr : def.descriptionEn}
        />
        <div className="flex flex-wrap items-center gap-2">
          {canIssue && (
            <Button
              loading={busy === "issue"}
              onClick={() =>
                run("issue", () => apiFetch(`/invoices/${id}/issue`, { method: "POST", body: {} }),
                  ar ? "صدرت الفاتورة." : "Invoice issued.")
              }
            >
              {ar ? "إصدار الفاتورة" : "Issue invoice"}
            </Button>
          )}

          {canPay && (
            <Button variant="outline" onClick={() => { setPayOpen(true); setPayError(null); }}>
              {ar ? "تسجيل دفعة" : "Record payment"}
            </Button>
          )}

          {canPrint && (
            <div className="flex items-center gap-2">
              <Select
                value={format}
                onChange={(e) => setFormat(e.target.value as InvoiceFormat)}
                aria-label={ar ? "صيغة الطباعة" : "Print format"}
              >
                {FORMATS.map((f) => (
                  <option key={f.key} value={f.key}>{ar ? f.ar : f.en}</option>
                ))}
              </Select>
              <Button
                variant="outline"
                icon={<Printer className="size-4" />}
                onClick={() => setPrinting(format)}
              >
                {ar ? "طباعة" : "Print"}
              </Button>
            </div>
          )}

          {canDownloadPdf && (
            <Button
              variant="outline"
              icon={<Download className="size-4" />}
              loading={busy === "download"}
              onClick={async () => {
                setActionError(null);
                setBusy("download");
                try {
                  await downloadFile(
                    `/invoices/${id}/pdf?lang=${language}`,
                    `${invoice.invoiceRef || "draft-invoice"}.pdf`,
                  );
                } catch (err) {
                  if (err instanceof ApiError && err.code === "pdf_generation_unavailable") {
                    setActionError(
                      ar
                        ? "توليد PDF من الخادم غير مُفعّل على هذا التثبيت. استخدم زر الطباعة بدلاً من ذلك."
                        : "Server-side PDF generation is not enabled on this deployment. Use Print instead.",
                    );
                  } else {
                    setActionError(
                      err instanceof ApiError ? err.message : ar ? "تعذّر تنزيل الملف." : "Couldn't download the file.",
                    );
                  }
                } finally {
                  setBusy(null);
                }
              }}
            >
              {ar ? "تنزيل PDF" : "Download PDF"}
            </Button>
          )}

          {canRegeneratePdf && (
            <Button variant="ghost" onClick={() => { setRegenOpen(true); setRegenReason(""); setRegenError(null); }}>
              {ar ? "إعادة توليد PDF" : "Regenerate PDF"}
            </Button>
          )}

          {canVoid && (
            <Button variant="danger" onClick={() => setVoidOpen(true)}>
              {ar ? "إبطال الفاتورة" : "Void invoice"}
            </Button>
          )}

          {canDelete && (
            <Button
              variant="ghost"
              loading={busy === "delete"}
              onClick={() =>
                run("delete", () => apiFetch(`/invoices/${id}`, { method: "DELETE" }),
                  ar ? "حُذفت المسودة." : "Draft deleted.")
              }
            >
              {ar ? "حذف المسودة" : "Delete draft"}
            </Button>
          )}
        </div>

        {actionError && <div className="mt-3"><ErrorText>{actionError}</ErrorText></div>}

        {/* The print dialog produces the PDF; saying so avoids promising a
            download button that does not exist. */}
        {canPrint && (
          <p className="mt-3 text-xs text-muted-foreground">
            {ar
              ? "للحصول على ملف PDF اختر «حفظ كملف PDF» من نافذة الطباعة."
              : "For a PDF, choose “Save as PDF” in the print dialog."}
          </p>
        )}
      </Card>

      {/* ── Money summary ───────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title={ar ? "الإجمالي" : "Total"} />
          <p className="tabular-nums text-2xl font-bold text-ink-900">
            {money(invoice.totals.totalMinor)} <span className="text-base font-medium">{currency}</span>
          </p>
        </Card>
        <Card>
          <CardHeader title={ar ? "المدفوع" : "Paid"} />
          <p className="tabular-nums text-2xl font-bold text-success">{money(invoice.totals.amountPaidMinor)}</p>
        </Card>
        <Card>
          <CardHeader title={ar ? "المستحق" : "Balance due"} />
          <p className={`tabular-nums text-2xl font-bold ${invoice.totals.balanceDueMinor > 0 ? "text-warning" : "text-ink-900"}`}>
            {money(invoice.totals.balanceDueMinor)}
          </p>
        </Card>
      </div>

      {/* ── The document itself, exactly as it will print ────────────────── */}
      <Card padding="none">
        <div className="p-5 pb-0 sm:p-6 sm:pb-0">
          <CardHeader
            title={ar ? "المستند" : "Document"}
            description={ar ? "معاينة مطابقة للطباعة." : "A true preview of the printed document."}
          />
        </div>
        <div className="overflow-x-auto bg-ink-100 p-5 sm:p-6">
          <div className="mx-auto w-fit shadow-md">
            <InvoiceDocument
              invoice={invoice}
              format={format}
              language={docLanguage}
              logoUrl={logoUrl}
              footerText={docFooter}
              presentation={toPresentation(settings)}
            />
          </div>
        </div>
      </Card>

      {invoice.orderRef && (
        <p className="text-xs text-muted-foreground">
          {ar ? "من الطلب: " : "From order: "}
          <Link to="/orders" className="text-accent-600 hover:underline">{invoice.orderRef}</Link>
        </p>
      )}

      {/* ── Void ────────────────────────────────────────────────────────── */}
      <Modal
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        title={ar ? "إبطال الفاتورة" : "Void this invoice"}
        closeLabel={ar ? "إغلاق" : "Close"}
      >
        <p className="text-sm text-ink-700">
          {ar
            ? "يبقى المستند وسجلّه محفوظًا، ولا يُحتسب ضمن الإيرادات. لا يمكن التراجع."
            : "The document and its history are kept, but excluded from revenue. This cannot be undone."}
        </p>
        <div className="mt-3">
          <Input
            label={ar ? "سبب الإبطال" : "Reason"}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            required
          />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setVoidOpen(false)}>{ar ? "تراجع" : "Keep it"}</Button>
          <Button
            variant="danger"
            loading={busy === "void"}
            disabled={voidReason.trim().length < 3}
            onClick={async () => {
              const ok = await run("void",
                () => apiFetch(`/invoices/${id}/void`, { method: "POST", body: { reason: voidReason.trim() } }),
                ar ? "أُبطلت الفاتورة." : "Invoice voided.");
              if (ok) { setVoidOpen(false); setVoidReason(""); }
            }}
          >
            {ar ? "تأكيد الإبطال" : "Void invoice"}
          </Button>
        </div>
      </Modal>

      {/* ── Payment ─────────────────────────────────────────────────────── */}
      <Modal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title={ar ? "تسجيل دفعة" : "Record a payment"}
        closeLabel={ar ? "إغلاق" : "Close"}
      >
        <div className="space-y-3">
          <Input
            label={`${ar ? "المبلغ" : "Amount"} (${currency})`}
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            inputMode="decimal"
            placeholder="0.00"
          />
          <Select
            label={ar ? "طريقة الدفع" : "Method"}
            value={payMethod}
            onChange={(e) => setPayMethod(e.target.value as PaymentMethod)}
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {ar ? PAYMENT_METHOD_DEFINITIONS[m].ar : PAYMENT_METHOD_DEFINITIONS[m].en}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">
            {ar ? "المتبقي: " : "Outstanding: "}
            <span className="tabular-nums">{money(invoice.totals.balanceDueMinor)} {currency}</span>
          </p>
          {payError && <ErrorText>{payError}</ErrorText>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setPayOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button>
          <Button
            loading={busy === "pay"}
            onClick={async () => {
              setPayError(null);
              let amountMinor: number;
              try {
                // Exact decimal parsing — never parseFloat, which would make
                // 10.10 into 1009 halalas often enough to matter.
                amountMinor = parseMajorToMinor(payAmount.trim());
              } catch {
                setPayError(ar ? "أدخل مبلغًا صحيحًا." : "Enter a valid amount.");
                return;
              }
              if (amountMinor <= 0) {
                setPayError(ar ? "يجب أن يكون المبلغ أكبر من صفر." : "The amount must be greater than zero.");
                return;
              }
              const ok = await run("pay",
                () => apiFetch(`/invoices/${id}/payments`, {
                  method: "POST",
                  body: { amountMinor, method: payMethod },
                }),
                ar ? "سُجّلت الدفعة." : "Payment recorded.");
              if (ok) { setPayOpen(false); setPayAmount(""); }
            }}
          >
            {ar ? "تسجيل" : "Record payment"}
          </Button>
        </div>
      </Modal>

      {/* ── Regenerate PDF ──────────────────────────────────────────────── */}
      <Modal
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        title={ar ? "إعادة توليد ملف PDF" : "Regenerate the PDF"}
        closeLabel={ar ? "إغلاق" : "Close"}
      >
        <p className="text-sm text-ink-700">
          {ar
            ? "تُنشأ نسخة جديدة من الملف؛ النسخة الحالية تبقى محفوظة ولا تُحذف."
            : "This creates a new version of the file. The current version is kept, never deleted."}
        </p>
        <div className="mt-3">
          <Input
            label={ar ? "سبب إعادة التوليد" : "Reason"}
            value={regenReason}
            onChange={(e) => setRegenReason(e.target.value)}
            required
          />
        </div>
        {regenError && <div className="mt-3"><ErrorText>{regenError}</ErrorText></div>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setRegenOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button>
          <Button
            loading={busy === "regenerate"}
            disabled={regenReason.trim().length < 3}
            onClick={async () => {
              setRegenError(null);
              setBusy("regenerate");
              try {
                await apiFetch(`/invoices/${id}/pdf/regenerate`, {
                  method: "POST",
                  body: { reason: regenReason.trim(), language },
                });
                showToast("success", ar ? "أُعيد توليد الملف." : "The PDF was regenerated.");
                setRegenOpen(false);
                setRegenReason("");
              } catch (err) {
                setRegenError(
                  err instanceof ApiError ? err.message : ar ? "تعذّرت إعادة التوليد." : "Couldn't regenerate the PDF.",
                );
              } finally {
                setBusy(null);
              }
            }}
          >
            {ar ? "تأكيد" : "Regenerate"}
          </Button>
        </div>
      </Modal>

      {/* Mounted only while printing, then removed by `afterprint`. */}
      {printing && (
        <PrintPortal format={printing} onDone={() => setPrinting(null)}>
          <InvoiceDocument
            invoice={invoice}
            format={printing}
            language={docLanguage}
            logoUrl={logoUrl}
            footerText={docFooter}
            presentation={toPresentation(settings)}
          />
        </PrintPortal>
      )}
    </div>
  );
}
